import {Button} from '@mui/material'
import {DatePicker, Form, Input, TimePicker} from 'antd'
import {FormattedMessage} from 'react-intl'
import PrimaryButton from 'src/components/elements/Buttons/PrimaryButton/PrimaryButton'
import SecondaryButton from 'src/components/elements/Buttons/SecondaryButton/SecondaryButton'
import DialogCustom from 'src/components/elements/DialogCustom/DialogCustom'
import locale from 'antd/es/date-picker/locale/it_IT'
import {useState, useEffect} from 'react'
import Icon from '@mdi/react'
import {mdiMagnify, mdiTrashCanOutline} from '@mdi/js'
import PatientLookupDialog from '../PatientLookupDialog/PatientLookupDialog'
import {VirtualMeetingOrganizeDialogStyled} from './VirtualMeetingOrganizeDialogStyled'
import moment from 'moment'
import DoctorsLookupDialog from '../DoctorsLookupDialog/DoctorsLookupDialog'
import {useAppSelector} from 'src/hooks'

type Participant = {
  user: Record<string, any>;
  participantUserGroup: 'PATIENT' | 'DOCTOR';
};

type VirtualMeetingOrganizeDialogProps = {
  participantUserGroup: string | string[],
  onClose: () => void,
  onSave: (values: any) => void;
};

const VirtualMeetingOrganizeDialog: React.FC<VirtualMeetingOrganizeDialogProps> = ({
  participantUserGroup,
  onClose,
  onSave
}) => {
  const [form] = Form.useForm()
  const {data: userInfoData} = useAppSelector(({userReducer}) => userReducer)
  const [isPatientsModal, setIsPatientsModal] = useState<boolean>(false)
  const [isDoctorsModal, setIsDoctorsModal] = useState<boolean>(false)

  const [participants, setParticipants] = useState<Participant[]>([])

  const onFinish = (values: any) => {
    const startDay = values.startDay

    delete values.startDay
    delete values.participants

    onSave({
      ...values,
      startDate: values.startDate.set({year: startDay.year(), month: startDay.month(), date: startDay.date()}).toISOString(),
      endDate: values.endDate.set({year: startDay.year(), month: startDay.month(), date: startDay.date()}).toISOString(),
      participants: participants.map(p => ({
        user: p.user,
        participantUserGroup: p.participantUserGroup
      }))
    })
  }

  useEffect(() => {
    form.setFieldsValue({participants: participants})
  }, [participants, form])

  const handleAddParticipant = (row: any, type: 'PATIENT' | 'DOCTOR') => {
    if (participants.some(p => p.user._id === row._id)) {
      return
    }

    const newParticipant: Participant = {
      user: row,
      participantUserGroup: type
    }
    setParticipants(prev => [...prev, newParticipant])

    setIsPatientsModal(false)
    setIsDoctorsModal(false)
  }

  const handleRemoveParticipant = (userId: string) => {
    setParticipants(prev => prev.filter(p => p.user._id !== userId))
  }

  const body = (
    <VirtualMeetingOrganizeDialogStyled>
      <Form
        form={form}
        id='organize-form'
        initialValues={{participants: []}}
        layout='vertical'
        onFinish={onFinish}
      >
        <Form.Item
          className='title'
          label='Titolo'
          name='title'
          rules={[{required: true, message: 'Campo obbligatorio'}]}
        >
          <Input />
        </Form.Item>
        <Form.Item
          className='startDay'
          label='Data evento'
          name='startDay'
          rules={[
            {required: true, message: 'Campo obbligatorio'},
            () => ({
              validator (_, value) {
                if (value?.isBefore(moment().startOf('day'))) {
                  return Promise.reject(new Error("L'appuntamento non può essere nel passato"))
                }
                return Promise.resolve()
              }
            })
          ]}
        >
          <DatePicker
            format='DD MMM YYYY'
            getPopupContainer={(trigger) => trigger}
            locale={locale}
            placeholder=''
          />
        </Form.Item>
        <Form.Item
          className='startDate'
          dependencies={['startDay']}
          label='Dalle'
          name='startDate'
          rules={[
            {required: true, message: 'Campo obbligatorio'},
            ({getFieldValue}) => ({
              validator (_, value) {
                const startDay = getFieldValue('startDay')
                if (value && startDay) {
                  const combinedStartDate = value.clone().set({year: startDay.year(), month: startDay.month(), date: startDay.date()})
                  if (combinedStartDate.isBefore(moment())) {
                    return Promise.reject(new Error("L'orario non può essere nel passato"))
                  }
                }
                return Promise.resolve()
              }
            })
          ]}
        >
          <TimePicker
            format='HH:mm'
            getPopupContainer={(trigger) => trigger}
            locale={locale}
            placeholder=''
          />
        </Form.Item>
        <Form.Item
          className='endDate'
          label='Alle'
          name='endDate'
          rules={[
            {required: true, message: 'Campo obbligatorio'},
            ({getFieldValue}) => ({
              validator (_, value) {
                const startDate = getFieldValue('startDate')
                if (!value || !startDate || value.isSameOrBefore(startDate)) {
                  return Promise.reject(new Error('L\'orario di fine deve essere successivo a quello di inizio'))
                }
                return Promise.resolve()
              }
            })
          ]}
        >
          <TimePicker
            format='HH:mm'
            getPopupContainer={(trigger) => trigger}
            locale={locale}
            placeholder=''
          />
        </Form.Item>

        <div className='organize-with'>
          <label className='ant-form-item-label'>{'Partecipanti'}</label>
          <div style={{display: 'flex', alignItems: 'center', gap: '16px'}}>
            {((Array.isArray(participantUserGroup) && participantUserGroup.includes('PATIENT')) || participantUserGroup === 'PATIENT') && (
              <Button
                className='add-participant-btn'
                onClick={() => setIsPatientsModal(true)}
                startIcon={<Icon path={mdiMagnify} size={0.8} />}
              >
                {'Aggiungi Paziente'}
              </Button>
            )}
            {((Array.isArray(participantUserGroup) && participantUserGroup.includes('DOCTOR')) || participantUserGroup === 'DOCTOR') && (
              <Button
                className='add-participant-btn'
                onClick={() => setIsDoctorsModal(true)}
                startIcon={<Icon path={mdiMagnify} size={0.8} />}
              >
                {'Aggiungi Medico'}
              </Button>
            )}
          </div>

          <Form.Item className='participants-validation' name='participants' rules={[{required: true, message: 'Aggiungere almeno un partecipante', type: 'array'}]} />

          <div className='participants-list'>
            {participants.map(p => (
              <div className='participant-item' key={p.user._id}>
                <span>
                  {p.user.firstName} {p.user.lastName}
                  {p.user.email && p.user.email !== '' ? ` - ${p.user.email} ` : ''}
                  {`(${p.participantUserGroup === 'PATIENT' ? 'Paziente' : 'Medico'})`}
                </span>
                <Button className='remove-btn' onClick={() => handleRemoveParticipant(p.user._id)}>
                  <Icon path={mdiTrashCanOutline} size={0.8} />
                </Button>
              </div>
            ))}
          </div>
        </div>
      </Form>
    </VirtualMeetingOrganizeDialogStyled>
  )

  const footer = (
    <div style={{gap: '16px', display: 'flex', width: '100%'}}>
      <PrimaryButton
        form='organize-form'
        key='submit'
        type='submit'
      >
        <FormattedMessage id='save' />
      </PrimaryButton>
      <SecondaryButton onClick={onClose}>
        <FormattedMessage id='undo' />
      </SecondaryButton>
    </div>
  )

  return (
    <>
      <DialogCustom
        body={body}
        footer={footer}
        maxWidth='xl'
        onClose={onClose}
        title={{text: 'Organizza incontro virtuale', format: false}}
      />
      {isPatientsModal &&
        <PatientLookupDialog
          onClose={() => setIsPatientsModal(false)}
          onRowClick={(row) => handleAddParticipant(row, 'PATIENT')}
        />}
      {isDoctorsModal &&
        <DoctorsLookupDialog
          customFilters={[{property: 'code', value: `!${userInfoData?.code}`}]}
          onClose={() => setIsDoctorsModal(false)}
          onRowClick={(row) => handleAddParticipant(row, 'DOCTOR')}
        />}
    </>
  )
}

export default VirtualMeetingOrganizeDialog
